import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Anchor, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useCalendar, useCalendarContext, type CalendarItem } from '../../../hooks/useCalendarApi';
import { itemTitle } from '../../../components/calendar/CalendarItemRow';
import { zonedDay } from '../../../components/calendar/layoutMonth';

/** Сколько дней вперёд показывать. Дальше — уже не «скоро», а раздел календаря. */
const HORIZON_DAYS = 21;
const MAX_ROWS = 4;

/**
 * Ближайшие сроки из календаря: что наступит в следующие три недели. Не сетка
 * месяца — на главной она заняла бы весь экран ради ответа «какое сегодня
 * число». Полный календарь живёт в своём разделе, сюда попадают ближайшие
 * даты и ссылка туда.
 */
export const UpcomingBlock: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const range = React.useMemo(() => {
    const now = dayjs();
    return {
      from: now.startOf('day').toISOString(),
      to: now.add(HORIZON_DAYS, 'day').toISOString(),
    };
  }, []);

  const { data: context } = useCalendarContext();
  const { data: items } = useCalendar(range.from, range.to, 'me');
  const timeZone = context?.timeZone;

  // У периода (стадия ревью, окно оценки) важен конец, а не начало: идущая
  // стадия — это «до 20 октября», а не «с 5 октября».
  const dateOf = (item: CalendarItem) =>
    item.type === 'period' && item.end
      ? dayjs(timeZone ? zonedDay(item.end, timeZone) : item.end).subtract(1, 'day')
      : dayjs(timeZone ? zonedDay(item.start, timeZone) : item.start);

  // Сделанное и просроченное сюда не идёт: просроченное показывает список дел
  // выше, а прошедшие даты ничего не подсказывают.
  const today = dayjs().startOf('day');
  const upcoming = (items ?? [])
    .filter((item) => !item.isDone && !item.isOverdue && !dateOf(item).isBefore(today))
    .sort((a, b) => dateOf(a).valueOf() - dateOf(b).valueOf())
    .slice(0, MAX_ROWS);

  if (upcoming.length === 0) return null;

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {t('dashboard.upcoming.title')}
      </Text>
      <Paper withBorder radius="md" className="todo-list">
        <Stack gap={0}>
          {upcoming.map((item) => (
            <UnstyledButton
              key={item.id}
              className="todo-list__item"
              onClick={() => navigate(item.url)}
            >
              <Group gap="sm" wrap="nowrap" align="center">
                <Text size="sm" style={{ flex: 1, minWidth: 0 }} truncate>
                  {itemTitle(t, item)}
                </Text>
                <Text size="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {/* День считаем в поясе организации: иначе у человека в другом
                      часовом поясе срок съезжает на сутки. */}
                  {dateOf(item).format('D MMM')}
                </Text>
                <IconChevronRight size={16} className="todo-list__chevron" />
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </Paper>
      <Anchor
        component="button"
        type="button"
        size="sm"
        onClick={() => navigate('/calendar')}
        style={{ alignSelf: 'flex-start' }}
      >
        {t('dashboard.upcoming.openCalendar')}
      </Anchor>
    </Stack>
  );
};
